import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import axios from "axios";

import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { journeyReportEndpiont } from "config";
import JourneyReportTable from "./journeyReportTable";
import CircularProgress from "@mui/material/CircularProgress";
import SingleDatePicker from "layouts/SingleDatePicker/singleDatePicker";
import DhundhooLoader from "../Loader/Loader";

function JourneyLogs() {
  const [journeyData, setJourneyData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const defaultDate = getTodayDateString();

  // ✅ Set default date as string format required by custom picker
  const [date, setDate] = useState(defaultDate);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Table columns
  const columns = [
    { Header: "Date", accessor: "dateRange", width: "10%" },
    { Header: "Vehicle Number", accessor: "thingId", width: "15%" },
    { Header: "Journey Name", accessor: "journeyName", width: "20%" },
    { Header: "Journey Type", accessor: "journeyType", disableSortBy: true ,
      Cell: ({ value }) => (
        <TypeBadge
          value={value}
          config={{}}
          defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
        />
      ),
    },
    { Header: "Journey Time", accessor: "timings", width: "10%" },
    { Header: "ETA Alerts", accessor: "etaCount", width: "10%" },
    { Header: "GeoFence Alerts", accessor: "geofenceCount", width: "10%" },
    { Header: "Number of Stops", accessor: "stopCount", width: "10%" },
    { Header: "Start Time", accessor: "startTime", width: "10%" },
    { Header: "End Time", accessor: "endTime", width: "10%" },
  ];

  const fetchLogs = async () => {
    if (!date) {
      setErrorMessage("Please select a date first.");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    try {
      const data = JSON.stringify({
        date: date, // ✅ Date is now exactly "YYYY-MM-DD"
        zone: "Asia/Kolkata",
      });

      const config = {
        method: "post",
        url: journeyReportEndpiont,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);

      const formattedRows = response.data.things.map((journey) => ({
        dateRange: journey.dateRange,
        thingId: journey.thingId,
        journeyName: journey.journeyName,
        journeyType: journey.journeyType,
        timings: journey.timings,
        etaCount: journey.etaCount || 0,
        geofenceCount: journey.geofenceCount || 0,
        stopCount: journey.stopCount || 0,
        startTime: journey.startTime,
        endTime: journey.endTime,
      }));

      setJourneyData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading journey logs");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (journeyData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

   const formattedDate = date
  ? date.split("-").reverse().join("-") // DD-MM-YYYY
  : "Export";

    const headers = [
      "Date",
      "Vehicle Number",
      "Journey Name",
      "Journey Type",
      "Journey Time",
      "ETA Alerts",
      "GeoFence Alerts",
      "Number of Stops",
      "Start Time",
      "End Time",
    ];

    const rows = journeyData.map((row) =>
      [
        row.dateRange,
        row.thingId,
        row.journeyName,
        row.journeyType,
        row.timings,
        row.etaCount,
        row.geofenceCount,
        row.stopCount,
        row.startTime,
        row.endTime,
      ].join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    // Fallback date string for CSV name
    const exportDate = date ? date : "Export";
    link.download = `JourneyLogs_${formattedDate}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setDate(defaultDate); // ✅ Reset back to today
    setJourneyData([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: "#fff",
                }}
              >
                <Grid container spacing={2} alignItems="center">

                  {/* ✅ Single Custom Date Picker */}
                  <Grid item xs={12} sm={6} md={3}>
                    <div style={{ width: '70%' }}>
                      <SingleDatePicker
                        date={date}
                        onChange={(newDate) => setDate(newDate)}
                        disabled={loading}
                      />
                    </div>
                  </Grid>

                  {/* Buttons */}
                  <Grid item xs={12} md="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="gradient"
                        color="warning"
                        size="small"
                        onClick={fetchLogs}
                        sx={{ minWidth: "100px" }}
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "LOAD"
                        )}
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleClear}
                        sx={{ minWidth: "100px" }}
                        disabled={loading}
                      >
                        CLEAR
                      </MDButton>
                      <DownloadExcelButton label="CSV" onClick={exportToCSV} disabled={loading || journeyData.length === 0} />
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Error alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </MDBox>
              )}

              {/* DataTable or Loader */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <JourneyReportTable
                    table={{ columns, rows: journeyData }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Journey Logs"
        content="Data Loaded Successfully"
        dateTime="Just now"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Journey Logs"
        content="Failed to load logs. Please try again."
        dateTime="Just now"
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default JourneyLogs;